import React from 'react';
import { Mic, Music, Volume2, Sparkles, Quote, Megaphone, Target, Users, ChevronRight } from 'lucide-react';

/**
 * AudioAnalysisPanel — futuristic gradient-border card with proper section spacing
 * and an animated waveform next to the detected audio language as a visual cue.
 *
 * Used by both Creative Analyzer (single-column or right rail) and Creative Log detail
 * (right rail). Sections are visually separated with hairline dividers; each block has
 * its own padding so dense content never feels cramped.
 */
export const AudioAnalysisPanel = ({ audioInfo }) => {
  if (!audioInfo) return null;
  const a = audioInfo.analysis || {};
  const hasTranscript = (audioInfo.transcription || '').trim().length > 0 && audioInfo.transcription !== 'Transcription unavailable';
  const hasAnyData = audioInfo.has_audio || hasTranscript || Object.keys(a).length > 0;
  if (!hasAnyData) {
    return (
      <div className="audio-panel-shell">
        <div className="audio-panel-inner p-4 text-xs text-slate-500" data-testid="audio-analysis-panel-empty">
          <span className="inline-flex items-center gap-1.5"><Volume2 className="h-3.5 w-3.5" /> No audio detected on this video</span>
        </div>
      </div>
    );
  }

  const Pill = ({ children, color = 'slate' }) => {
    const cls = {
      emerald: 'bg-emerald-100/80 text-emerald-700 ring-emerald-200',
      amber: 'bg-amber-100/80 text-amber-700 ring-amber-200',
      rose: 'bg-rose-100/80 text-rose-700 ring-rose-200',
      violet: 'bg-violet-100/80 text-violet-700 ring-violet-200',
      slate: 'bg-slate-100/80 text-slate-700 ring-slate-200',
    }[color];
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ring-1 ${cls}`}>{children}</span>;
  };

  const SectionLabel = ({ icon: Icon, children, tag }) => (
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3 text-slate-400" />} {children}
      </p>
      {tag}
    </div>
  );

  const strengthColor = ({ strong: 'emerald', moderate: 'amber', weak: 'rose' })[String(a.hook_strength || '').toLowerCase()] || 'slate';
  const pacingColor = ({ slow: 'slate', medium: 'amber', fast: 'rose' })[String(a.pacing || '').toLowerCase()] || 'slate';

  const rawTranscript = (audioInfo.transcription || '').trim();
  const englishTranscript = (a.transcript_english || '').trim();
  const hinglishTranscript = (a.transcript_hinglish || '').trim();

  // Which transcript(s) to show and how to label them.
  //   • Hinglish present → show Hinglish (existing behaviour, HINGLISH tag)
  //   • English translation present (Tamil / Telugu / … source) → show BOTH:
  //     the raw original transcript first, then the English translation with
  //     an "ENGLISH TRANSLATION" tag, so verbatim quotes are still available
  //   • Otherwise → show raw transcript as-is
  const showTranslation = englishTranscript.length > 0;
  const primaryTranscript = hinglishTranscript || rawTranscript;
  const primaryTag = hinglishTranscript
    ? 'HINGLISH'
    : (showTranslation && (audioInfo.language && audioInfo.language.toLowerCase() !== 'english')
        ? String(audioInfo.language).toUpperCase()
        : null);

  return (
    <div className="audio-panel-shell" data-testid="audio-analysis-panel">
      <div className="audio-panel-inner !p-0">
        {/* HEADER */}
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 via-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/40 shrink-0">
              <Mic className="h-5 w-5" strokeWidth={1.75} />
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-3.5 h-3.5">
                <Sparkles className="h-2.5 w-2.5 text-fuchsia-200 drop-shadow" />
              </span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700 leading-tight flex items-center gap-1.5">
                Audio Intelligence
                <span className="inline-block w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
              </p>
              {/* Language + animated waveform as visual cue */}
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[11.5px] font-semibold text-slate-700 capitalize">
                  {audioInfo.language || 'Unknown lang'}
                </span>
                <span className="audio-wave" aria-hidden>
                  <span /><span /><span /><span /><span />
                </span>
                <span className="text-[10.5px] text-slate-400">
                  {audioInfo.duration_seconds ? `${audioInfo.duration_seconds}s` : ''}
                  {typeof a.word_count === 'number' ? ` · ${a.word_count} words` : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Tone + pacing — separated row with breathing room */}
          {(a.tone || a.pacing) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-4">
              {a.tone && <Pill color="violet">Tone · {a.tone}</Pill>}
              {a.pacing && <Pill color={pacingColor}>Pacing · {a.pacing}</Pill>}
            </div>
          )}
        </div>

        {/* HOOK */}
        {a.hook && (
          <>
            <div className="audio-divider" />
            <div className="px-5 py-4">
              <SectionLabel icon={Quote} tag={a.hook_strength && <Pill color={strengthColor}>{a.hook_strength}</Pill>}>Hook</SectionLabel>
              <div className="relative p-3 rounded-lg bg-gradient-to-br from-fuchsia-50/70 to-pink-50/50 ring-1 ring-pink-100/80">
                <p className="text-[13px] text-slate-800 leading-relaxed italic">"{a.hook}"</p>
              </div>
            </div>
          </>
        )}

        {/* CTAs */}
        {Array.isArray(a.ctas) && a.ctas.length > 0 && (
          <>
            <div className="audio-divider" />
            <div className="px-5 py-4">
              <SectionLabel icon={Megaphone}>Calls to Action</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {a.ctas.map((c, i) => (
                  <span key={i} className="text-[12px] px-2.5 py-1 rounded-md bg-violet-50/90 text-violet-700 ring-1 ring-violet-100 leading-snug">{c}</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Key claims */}
        {Array.isArray(a.key_claims) && a.key_claims.length > 0 && (
          <>
            <div className="audio-divider" />
            <div className="px-5 py-4">
              <SectionLabel>Key Claims</SectionLabel>
              <ul className="space-y-1.5">
                {a.key_claims.map((k, i) => (
                  <li key={i} className="text-[12.5px] text-slate-700 flex items-start gap-2 leading-relaxed">
                    <span className="mt-2 w-1 h-1 rounded-full bg-violet-400 shrink-0" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* Key phrases */}
        {Array.isArray(a.key_phrases) && a.key_phrases.length > 0 && (
          <>
            <div className="audio-divider" />
            <div className="px-5 py-4">
              <SectionLabel>Memorable Phrases</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {a.key_phrases.map((k, i) => (
                  <span key={i} className="text-[11.5px] px-2 py-0.5 rounded bg-amber-50 text-amber-800 ring-1 ring-amber-100 italic leading-snug">"{k}"</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Target audience */}
        {a.target_audience && (
          <>
            <div className="audio-divider" />
            <div className="px-5 py-4">
              <SectionLabel icon={Target}>Target Audience</SectionLabel>
              <p className="text-[12.5px] text-slate-700 leading-relaxed flex items-start gap-1.5">
                <Users className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                <span>{a.target_audience}</span>
              </p>
            </div>
          </>
        )}

        {/* Sound notes */}
        {a.sound_notes && (
          <>
            <div className="audio-divider" />
            <div className="px-5 py-4">
              <SectionLabel icon={Music}>Sound Notes</SectionLabel>
              <p className="text-[12.5px] text-slate-700 leading-relaxed">{a.sound_notes}</p>
            </div>
          </>
        )}

        {/* Transcript (collapsible) — shows raw + optional English translation
            side-by-side for regional-language ads so verbatim quotes remain
            available alongside translated insights. */}
        {(primaryTranscript || englishTranscript) && (
          <>
            <div className="audio-divider" />
            <details className="group px-5 py-3.5" open={showTranslation}>
              <summary className="cursor-pointer text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700 select-none flex items-center justify-between">
                <span className="inline-flex items-center gap-2">
                  Full transcript
                  {primaryTag && <span className="px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 text-[9px] tracking-wider ring-1 ring-fuchsia-100">{primaryTag}</span>}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-90" />
              </summary>
              {primaryTranscript && (
                <p className="mt-3 p-3 rounded-md bg-slate-50/80 text-[12.5px] text-slate-700 whitespace-pre-wrap leading-relaxed ring-1 ring-slate-100" data-testid="audio-transcript">
                  {primaryTranscript}
                </p>
              )}
              {showTranslation && (
                <div className="mt-3" data-testid="audio-transcript-english">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1.5 flex items-center gap-1.5">
                    English translation
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] tracking-wider ring-1 ring-emerald-100">EN</span>
                  </p>
                  <p className="p-3 rounded-md bg-emerald-50/40 text-[12.5px] text-slate-800 whitespace-pre-wrap leading-relaxed ring-1 ring-emerald-100">
                    {englishTranscript}
                  </p>
                </div>
              )}
            </details>
          </>
        )}
      </div>
    </div>
  );
};

export default AudioAnalysisPanel;
